/**
 * @fileoverview TimeTracker context + hook (MOR-906)
 *
 * Split out of `TimeTrackerProvider.tsx` (react-refresh/only-export-components)
 * — read/control the global timer and quick entry dialog. Returns inert
 * defaults when no provider is mounted so consumers rendered outside the
 * dashboard shell don't crash.
 */

import { createContext, useContext } from "react";

interface RunningTimer {
  /** Epoch milliseconds the stopwatch was started. */
  startedAt: number;
  /** Matter the user was viewing when they started, used to prefill the dialog. */
  jobId?: string;
}

/**
 * Values handed to the quick entry dialog when it opens. Elapsed time is
 * passed as minutes rather than units because the firm's unit size lives
 * in `entryContext`, which only the dialog loads.
 *
 * Everything below `narrative` comes from a natural-language parse, which is
 * why each field is optional: the parser resolves what it can and leaves the
 * rest to the practitioner.
 */
export interface QuickEntryPrefill {
  jobId?: string;
  elapsedMinutes?: number;
  narrative?: string;
  date?: string;
  categoryId?: string;
  units?: number;
  billable?: boolean;
  projectCode?: "pre" | "post";
  /**
   * Clock window (HH:MM) the work actually occupied. Only set when it was
   * measured — the stopwatch knows when it ran, a typed entry does not — so
   * the dialog's times section stays empty rather than inventing a window.
   */
  startTime?: string;
  endTime?: string;
  /** Field names the parser could not work out, surfaced in the dialog. */
  unresolved?: string[];
}

export interface TimeTrackerContextValue {
  timer: RunningTimer | null;
  /** Whole seconds since the timer started; ticks once a second while running. */
  elapsedSec: number;
  startTimer: (jobId?: string) => void;
  /** Stops and clears the timer, returning what it recorded. */
  stopTimer: () => {
    elapsedMinutes: number;
    jobId?: string;
    /** Epoch bounds of the session, so the entry can keep the real window. */
    startedAt: number;
    stoppedAt: number;
  } | null;
  discardTimer: () => void;
  quickEntryOpen: boolean;
  quickEntryPrefill: QuickEntryPrefill | null;
  openQuickEntry: (prefill?: QuickEntryPrefill) => void;
  /**
   * Loads several parsed entries into the dialog, one at a time. Saving one
   * advances to the next instead of closing, so a sentence that described
   * three pieces of work does not quietly lose two of them.
   */
  openQuickEntryBatch: (prefills: QuickEntryPrefill[]) => void;
  /** How many entries are still waiting behind the one on screen. */
  quickEntryRemaining: number;
  /** Loads the next queued entry. Returns false when the queue is empty. */
  advanceQuickEntry: () => boolean;
  closeQuickEntry: () => void;
  /** The plain-English composer dialog that feeds the queue above. */
  nlEntryOpen: boolean;
  openNlEntry: () => void;
  closeNlEntry: () => void;
}

export const TimeTrackerContext = createContext<TimeTrackerContextValue | null>(
  null,
);

/**
 * Read/control the global timer and quick entry dialog. Returns inert
 * defaults when no provider is mounted so consumers rendered outside the
 * dashboard shell don't crash.
 */
export function useTimeTracker(): TimeTrackerContextValue {
  const ctx = useContext(TimeTrackerContext);
  if (ctx) return ctx;
  const noop = () => {};
  return {
    timer: null,
    elapsedSec: 0,
    startTimer: noop,
    stopTimer: () => null,
    discardTimer: noop,
    quickEntryOpen: false,
    quickEntryPrefill: null,
    openQuickEntry: noop,
    openQuickEntryBatch: noop,
    quickEntryRemaining: 0,
    advanceQuickEntry: () => false,
    closeQuickEntry: noop,
    nlEntryOpen: false,
    openNlEntry: noop,
    closeNlEntry: noop,
  };
}
